import i18n from '@/i18n'
import { INetwork } from '@/types'
import { getPreferredBtcAccount, isAddress, isMobileDevice, SUPPORTED_NETWORK, WALLET_XVERSE } from '@/utils'
import { CustomError } from '@/utils/error'

import { BaseWallet } from './BaseWallet'
import {
  AddressPurpose,
  AddressResponse,
  IWalletConnectOptions,
  PsbtSignOptions,
  TransactionOptions,
  XverseBitcoinProvider,
  XverseEventData,
} from './types'

const NETWORK_MAP: Record<string, string> = {
  Mainnet: SUPPORTED_NETWORK.BTC,
  Testnet: SUPPORTED_NETWORK.BTC_TESTNET,
}

function getProvider(): XverseBitcoinProvider | undefined {
  return window?.XverseProviders?.BitcoinProvider || undefined
}

export class XverseWallet extends BaseWallet {
  name = 'Xverse'
  key = WALLET_XVERSE
  private provider: XverseBitcoinProvider | null = null
  private connectedAddresses: Record<string, string> = {}
  private connectedPublicKeys: Record<string, string> = {}
  private currentNetwork: string | null = null
  private isConnecting = false
  private removeAccountChangeListener: (() => void) | null = null
  private removeNetworkChangeListener: (() => void) | null = null
  private removeAccountDisconnectedListener: (() => void) | null = null
  private accounts: AddressResponse[] = []

  private static readonly ERRORS = {
    NETWORK_REQUIRED: 'Network must be provided',
    CONNECTING: 'Xverse Wallet opening...',
    REDIRECTING: 'Redirecting to Xverse Wallet...',
    NO_PAYMENT_ADDRESS: 'No payment address found',
  }

  private redirectToWallet(): void {
    window.location.href = `xverse://browser?url=${encodeURIComponent(window.location.href)}`
  }

  private download(): void {
    this.isConnecting = false
    if (isMobileDevice()) throw new CustomError(i18n.t('wallet_error.XVERSE_MOBILE_ERROR'))
    throw new CustomError(
      i18n.t('wallet_error.NOT_INSTALLED_ERROR', { wallet: this.name, url: 'https://xverse.app/download' })
    )
  }

  isDetected(): boolean {
    return !!window.XverseProviders
  }

  initialize(): void {
    if (!this.provider) {
      this.provider = getProvider() || null
    }
    if (!this.provider) {
      this.download()
    }
  }

  isInstalled(): boolean {
    return !!window.XverseProviders
  }

  private async checkAndSwitchNetwork(networkId: string): Promise<void> {
    if (!this.provider) {
      this.isConnecting = false
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }

    const addressResponse = await this.provider.request('getAddresses', {
      purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
    })

    const paymentAddress = addressResponse?.result?.addresses?.[0]?.address

    if (!paymentAddress) {
      this.isConnecting = false
      throw new Error(`Please switch to ${networkId} in your Xverse wallet settings`)
    }

    if (!isAddress(paymentAddress, networkId)) {
      this.isConnecting = false
      throw new Error(
        `Please switch to Bitcoin ${networkId === SUPPORTED_NETWORK.BTC ? 'Mainnet' : 'Testnet'} in your Xverse wallet settings`
      )
    }
  }

  private setupEventListeners({ onAccountChanged, network, onDisconnected }: IWalletConnectOptions): void {
    if (!this.provider) {
      this.isConnecting = false
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }

    this.removeEventListeners()

    if (typeof this.provider.addListener !== 'function') {
      console.warn('Xverse Wallet event listeners not available')
      return
    }

    this.removeAccountChangeListener = this.provider.addListener('accountChange', async (_event: XverseEventData) =>
      this.handleAccountChange(network, onAccountChanged, onDisconnected)
    )

    this.removeNetworkChangeListener = this.provider.addListener('networkChange', async (event: XverseEventData) =>
      this.handleNetworkChange(event, network, onAccountChanged)
    )

    this.removeAccountDisconnectedListener = this.provider.addListener(
      'accountDisconnected',
      (_event: XverseEventData) => {
        onDisconnected?.(this.key)
        this.removeEventListeners()
      }
    )
  }

  private async handleAccountChange(
    network: INetwork,
    onAccountChanged?: (data: { address: string; networkInfo: INetwork }) => void,
    onDisconnected?: (key: string) => void
  ): Promise<void> {
    try {
      if (!this.provider) return

      const addressResponse = await this.provider.request('getAddresses', {
        purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
      })

      if (!addressResponse.error && addressResponse?.result?.addresses?.length) {
        const paymentAddress = await this.selectDefaultAccounts(addressResponse.result.addresses, network.networkId)

        if (paymentAddress) {
          this.setAddress(network.networkId, paymentAddress.address, paymentAddress.publicKey)
          onAccountChanged?.({
            address: paymentAddress.address,
            networkInfo: network,
          })
          return
        }
      }

      try {
        this.isConnecting = true
        const permissionResponse = await this.provider.request('wallet_requestPermissions', undefined)

        if (!permissionResponse.error) {
          const newAddressResponse = await this.provider.request('getAddresses', {
            purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
          })

          if (!newAddressResponse.error && newAddressResponse?.result?.addresses?.length) {
            const newPaymentAddress = await this.selectDefaultAccounts(
              newAddressResponse.result.addresses,
              network.networkId
            )
            if (newPaymentAddress) {
              this.setAddress(network.networkId, newPaymentAddress.address, newPaymentAddress.publicKey)
              onAccountChanged?.({
                address: newPaymentAddress.address,
                networkInfo: network,
              })
              return
            }
          }
        }

        delete this.connectedAddresses[network.networkId]
        onDisconnected?.(this.key)
      } catch (reconnectError) {
        console.error('Error reconnecting wallet:', reconnectError)
      } finally {
        this.isConnecting = false
      }
    } catch (error) {
      console.error('Error handling account change:', error)
    }
  }

  private async handleNetworkChange(
    event: XverseEventData,
    network: INetwork,
    onAccountChanged?: (data: { address: string; networkInfo: INetwork }) => void
  ): Promise<void> {
    try {
      if (!this.provider || !event.network) return

      const mappedNetwork = NETWORK_MAP[event.network] || network.networkId
      if (mappedNetwork === this.currentNetwork) return

      this.currentNetwork = mappedNetwork

      const addressResponse = await this.provider.request('getAddresses', {
        purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
      })
      if (addressResponse.error || !addressResponse?.result?.addresses?.length) return

      const paymentAddress = await this.selectDefaultAccounts(addressResponse.result.addresses, network.networkId)
      if (!paymentAddress) return

      this.setAddress(network.networkId, paymentAddress.address, paymentAddress.publicKey)
      onAccountChanged?.({
        address: paymentAddress.address,
        networkInfo: network,
      })
    } catch (error) {
      console.error('Error handling network change:', error)
    }
  }

  private removeEventListeners(): void {
    this.removeAccountChangeListener?.()
    this.removeAccountChangeListener = null

    this.removeNetworkChangeListener?.()
    this.removeNetworkChangeListener = null

    this.removeAccountDisconnectedListener?.()
    this.removeAccountDisconnectedListener = null
  }

  private async selectDefaultAccounts(
    addresses: AddressResponse[],
    networkId: string
  ): Promise<AddressResponse | undefined> {
    this.accounts = addresses
    return await getPreferredBtcAccount(addresses, networkId)
  }

  private setAddress(networkId: string, address: string, publicKey: string): void {
    this.connectedAddresses[networkId] = address
    this.connectedPublicKeys[networkId] = publicKey
  }

  async connect(
    network: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (this.isConnecting) {
      throw new CustomError(XverseWallet.ERRORS.CONNECTING)
    }

    this.isConnecting = true
    this.initialize()

    if (isMobileDevice() && !this.provider) {
      this.isConnecting = false
      this.redirectToWallet()
      throw new CustomError(XverseWallet.ERRORS.REDIRECTING)
    }

    if (!this.provider) {
      this.isConnecting = false
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }

    try {
      const permissionResponse = await this.provider.request('wallet_requestPermissions', undefined)
      if (permissionResponse.error) {
        throw new Error(permissionResponse.error.message)
      }

      this.setupEventListeners(options)

      const response = await this.provider.request('getAddresses', {
        purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
        message: 'Connect to app',
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      const paymentAddress = await this.selectDefaultAccounts(response.result.addresses, network)

      if (!paymentAddress) {
        throw new CustomError(XverseWallet.ERRORS.NO_PAYMENT_ADDRESS)
      }
      this.setAddress(network, paymentAddress.address, paymentAddress.publicKey)

      await this.checkAndSwitchNetwork(network)

      return {
        address: paymentAddress.address,
        networkInfo: options.network,
        compressedPublicKey: paymentAddress.publicKey,
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error))
    } finally {
      this.isConnecting = false
    }
  }

  async disconnect(): Promise<void> {
    try {
      await this.provider?.request('wallet_renouncePermissions', undefined)
    } catch (error) {
      console.warn('Error during disconnect:', error)
    } finally {
      this.removeEventListeners()
    }
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    network: string,
    _options?: TransactionOptions
  ): Promise<string> {
    if (!this.isInstalled()) {
      throw new CustomError('Xverse Wallet is not installed')
    }

    if (!this.provider) {
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }
    if (network) {
      await this.checkAndSwitchNetwork(network)
    }

    const response = await this.provider.request('sendTransfer', {
      recipients: [
        {
          address: toAddress,
          amount: Number(amount),
        },
      ],
    })

    if (response.result?.txid) {
      return response.result.txid
    }

    throw new Error('No transaction ID received')
  }

  async getAddress(networkId: string): Promise<string> {
    return this.connectedAddresses[networkId] ?? ''
  }

  getAddresses(): string[] {
    return this.accounts.map((acc) => acc.address)
  }

  switchWalletAddress(address: string, networkId: string) {
    const account = this.accounts.find((e) => e.address === address)
    if (account) {
      this.setAddress(networkId, account.address, account.publicKey)
    }
  }

  async signMessage(message: string, network?: string): Promise<string> {
    if (!this.isInstalled()) {
      throw new CustomError('Xverse Wallet is not installed')
    }

    if (!this.provider) {
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }

    if (network) {
      await this.checkAndSwitchNetwork(network)
    }

    const response = await this.provider.request('signMessage', {
      address: await this.getAddress(network ?? ''),
      message: message,
    })

    if (response.error) {
      throw new Error(response.error.message)
    }

    return response.result.signature
  }

  async getCompressedPublicKey(network?: string): Promise<string> {
    return this.connectedPublicKeys[network ?? ''] ?? ''
  }

  async signPsbt(psbt: string, network: string, options?: PsbtSignOptions): Promise<string> {
    if (!this.provider) {
      throw new CustomError(XverseWallet.ERRORS.NETWORK_REQUIRED)
    }

    if (network) {
      await this.checkAndSwitchNetwork(network)
    }

    const signInputs: Record<string, number[]> = {}

    const address = await this.getAddress(network ?? '')

    if (options?.toSignInputs) {
      options.toSignInputs.forEach((input) => {
        if (input.address && this.getAddresses().includes(input.address)) {
          if (!signInputs[input.address]) {
            signInputs[input.address] = []
          }
          signInputs[input.address].push(input.index)
        }
      })

      if (Object.keys(signInputs).length === 0) {
        signInputs[address] = options.toSignInputs.map((input) => input.index)
      }
    } else {
      signInputs[address] = [0]
    }

    const response = await this.provider.request('signPsbt', {
      psbt,
      signInputs,
      broadcast: options?.autoFinalized || false,
      allowedSignHash: options?.toSignInputs?.[0]?.sighashTypes?.[0],
    })

    if (response.error) {
      throw new Error(response.error.message)
    }

    return response.result.txid || response.result.psbt
  }

  priority(): number {
    const isXverseWallet = (): boolean => {
      try {
        return typeof window !== 'undefined' && /Xverse/i.test(navigator?.userAgent)
      } catch {
        return false
      }
    }

    if ((isXverseWallet() || this.isInstalled()) && isMobileDevice()) {
      return 0
    }
    return this.isInstalled() ? 0 : 1
  }
}
